import { html, nothing } from "lit";
import type { FleetTodoItem } from "../controllers/fleet-todos.ts";
import type { RunArtifactPreviewEntry } from "../controllers/run-artifact-previews.ts";
import { renderHighlightedDiffLine, classifyDiffLine } from "./chat-file-diff-viewer.ts";
import type { LiveRunStep } from "./chat-live-run.ts";

export type FleetUnitActivityDetail = {
  label: string;
  failureReason?: string;
  deliverablePath?: string;
  validationNotes?: string[];
};

export function unitDetailsFromFleetTodos(todos: FleetTodoItem[]): FleetUnitActivityDetail[] {
  return todos
    .filter(
      (t) =>
        Boolean(t.failureReason?.trim()) ||
        Boolean(t.deliverablePath?.trim()) ||
        (t.validationNotes?.length ?? 0) > 0,
    )
    .map((t) => ({
      label: t.content,
      failureReason: t.failureReason,
      deliverablePath: t.deliverablePath,
      validationNotes: t.validationNotes,
    }));
}

export type FleetRunActivityStripProps = {
  runActive: boolean;
  activityLabel?: string | null;
  /**
   * Sticky fallback for `activityLabel`. While the gateway briefly clears
   * the hint between phases, the chip should keep the previous non-empty
   * value. JAISIU-3153.
   */
  activityStickyLabel?: string | null;
  steps?: LiveRunStep[];
  filePreviews?: RunArtifactPreviewEntry[];
  onFileClick?: (changeId: string, path: string, artifactSessionKey?: string) => void;
  /** O-13 — unit-level validation / failure / deliverable from matching work units. */
  unitDetails?: FleetUnitActivityDetail[];
};

function renderPreviewLines(lines: string[]) {
  if (lines.length === 0) {
    return nothing;
  }
  return html`
    <pre class="fleet-run-activity__preview mono">
      ${lines.slice(0, 3).map((line) => renderHighlightedDiffLine(classifyDiffLine(line)))}
    </pre>
  `;
}

export function renderFleetRunActivityStrip(props: FleetRunActivityStripProps) {
  if (!props.runActive) {
    return nothing;
  }
  const label = props.activityLabel?.trim();
  const sticky = props.activityStickyLabel?.trim();
  const steps = props.steps ?? [];
  const files = props.filePreviews ?? [];
  const unitDetails = props.unitDetails ?? [];
  const activeStep = steps.find((s) => s.state === "active");
  // First non-empty label latches via the caller's `activityStickyLabel`;
  // only a different non-empty label replaces it. The `null → null`
  // cycle is dropped. JAISIU-3153.
  const displayLabel = label ?? sticky ?? activeStep?.label ?? "Working…";

  return html`
    <div class="fleet-run-activity" role="status" aria-live="polite">
      <div class="fleet-run-activity__current">
        <span class="fleet-run-activity__spinner" aria-hidden="true">◐</span>
        <span class="fleet-run-activity__label">${displayLabel}</span>
        ${
          activeStep?.detail
            ? html`<span class="fleet-run-activity__detail muted">${activeStep.detail}</span>`
            : nothing
        }
      </div>
      ${
        files.length > 0
          ? html`
              <ul class="fleet-run-activity__files" role="list">
                ${files.map(
                  (f) => html`
                    <li class="fleet-run-activity__file" role="listitem">
                      <button
                        type="button"
                        class="fleet-run-activity__file-btn"
                        @click=${() => props.onFileClick?.(f.changeId, f.path, f.sessionKey)}
                        ?disabled=${!f.changeId || !props.onFileClick}
                      >
                        <span
                          class="fleet-run-activity__file-op fleet-run-activity__file-op--${f.op}"
                          >${f.op === "manual" ? "modified" : f.op}</span
                        >
                        <span class="fleet-run-activity__file-path mono">${f.path}</span>
                        <span class="fleet-run-activity__file-stats muted">
                          +${f.additions} −${f.deletions}
                        </span>
                      </button>
                      ${renderPreviewLines(f.previewLines)}
                    </li>
                  `,
                )}
              </ul>
            `
          : nothing
      }
      ${
        unitDetails.length > 0
          ? html`
              <ul class="fleet-run-activity__units" role="list">
                ${unitDetails.map(
                  (d) => html`
                    <li class="fleet-run-activity__unit" role="listitem">
                      <span class="fleet-run-activity__unit-label">${d.label}</span>
                      ${
                        d.failureReason
                          ? html`<span class="fleet-run-activity__unit-fail"
                              >${d.failureReason}</span
                            >`
                          : nothing
                      }
                      ${
                        d.deliverablePath
                          ? html`<span class="fleet-run-activity__unit-path mono"
                              >${d.deliverablePath}</span
                            >`
                          : nothing
                      }
                      ${
                        d.validationNotes?.length
                          ? html`<span class="muted"
                              >${d.validationNotes[d.validationNotes.length - 1]}</span
                            >`
                          : nothing
                      }
                    </li>
                  `,
                )}
              </ul>
            `
          : nothing
      }
    </div>
  `;
}
